import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, X, Clock, Users, MapPin,
  Filter, Ban, Loader2, Pencil, Trash2, GripVertical, Save, Truck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TOUR_COLORS_MAP = {
  'tour-express': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', dot: 'bg-blue-500' },
  'madrid-capital-imperio': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', dot: 'bg-purple-500' },
  'madrid-siglos': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', dot: 'bg-amber-500' },
  'madrid-ventas-bernabeu': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300', dot: 'bg-rose-500' },
  'un-dia-madrid': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', dot: 'bg-emerald-500' }
};

const STATUS_COLORS = {
  confirmed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmada' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelada' },
  completed: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Completada' }
};

const DEFAULT_COLORS = [
  { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-300', dot: 'bg-sky-500' },
  { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300', dot: 'bg-pink-500' },
  { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', dot: 'bg-teal-500' },
];

const getTourColor = (tourId) => TOUR_COLORS_MAP[tourId] || DEFAULT_COLORS[Math.abs(tourId?.charCodeAt(0) || 0) % DEFAULT_COLORS.length] || DEFAULT_COLORS[0];

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];

// === BOOKING CHIP (draggable) ===
const BookingChip = ({ b, compact, onDragStart, onDragEnd, onEdit }) => {
  const tc = getTourColor(b.tour_id);
  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
  const statusDot = sc.bg === 'bg-green-100' ? 'bg-green-500' : sc.bg === 'bg-yellow-100' ? 'bg-yellow-500' : sc.bg === 'bg-red-100' ? 'bg-red-500' : 'bg-slate-400';
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, b)}
      onDragEnd={onDragEnd}
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate cursor-grab active:cursor-grabbing ${tc.bg} ${tc.text} border-l-2 ${statusDot === 'bg-green-500' ? 'border-l-green-500' : statusDot === 'bg-yellow-500' ? 'border-l-yellow-500' : statusDot === 'bg-red-500' ? 'border-l-red-500' : 'border-l-slate-400'} hover:ring-1 hover:ring-[#C2410C]/30 transition-all`}
      onClick={(e) => { e.stopPropagation(); onEdit(b); }}
      data-testid={`booking-chip-${b.id}`}
      title={`${b.name} - ${b.time} - Arrastrar para mover`}
    >
      {compact ? `${b.time} ${b.name?.split(' ')[0]}` : b.name?.split(' ')[0]}
    </div>
  );
};

const AdminCalendar = () => {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [tukTuks, setTukTuks] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tourFilter, setTourFilter] = useState('all');
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dragBooking, setDragBooking] = useState(null);
  const [newBooking, setNewBooking] = useState({
    tour_id: '', date: '', time: '10:00', adults: 1, children: 0,
    name: '', email: '', phone: '', notes: '', payment_method: 'cash', tuk_tuk_id: ''
  });
  const [blockForm, setBlockForm] = useState({ tour_id: '', date: '', time: '', reason: '' });

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const getDateRange = useCallback(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    if (view === 'month') {
      return { start: new Date(y, m, 1).toISOString().split('T')[0], end: new Date(y, m + 1, 0).toISOString().split('T')[0] };
    } else if (view === 'week') {
      const day = currentDate.getDay();
      const off = day === 0 ? -6 : 1 - day;
      const s = new Date(currentDate); s.setDate(currentDate.getDate() + off);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] };
    }
    const d = currentDate.toISOString().split('T')[0];
    return { start: d, end: d };
  }, [currentDate, view]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    try {
      const params = { start, end };
      if (tourFilter !== 'all') params.tour_id = tourFilter;
      const [bookingsRes, blocksRes, tukRes, toursRes] = await Promise.all([
        axios.get(`${API}/admin/bookings/calendar`, { headers, params }),
        axios.get(`${API}/admin/availability/blocks`, { headers, params: { start, end } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/tuktuks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/tours`).catch(() => ({ data: [] }))
      ]);
      setBookings(bookingsRes.data);
      setBlocks(blocksRes.data);
      setTukTuks(tukRes.data);
      const fetchedTours = toursRes.data;
      setTours(fetchedTours);
      // Set default tour_id for new booking if not yet set
      if (fetchedTours.length > 0) {
        setNewBooking(prev => prev.tour_id ? prev : { ...prev, tour_id: fetchedTours[0].id });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, tourFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const nav = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  // === EDIT BOOKING ===
  const openEdit = (b) => {
    setEditBooking(b);
    setEditForm({
      tour_id: b.tour_id || '', date: b.date || '', time: b.time || '',
      tuk_tuk_id: b.tuk_tuk_id || '', name: b.name || '', email: b.email || '',
      phone: b.phone || '', adults: b.adults || 1, children: b.children || 0,
      child_seats_needed: b.child_seats_needed || 0, status: b.status || 'pending',
      notes: b.notes || '', special_requests: b.special_requests || '',
      hotel: b.hotel || '', payment_status: b.payment_status || '',
      total_price: b.total_price || 0
    });
    setShowDeleteConfirm(false);
  };

  // Helper: get tour info from tours state
  const getTourInfo = (tourId) => tours.find(t => t.id === tourId);

  // When tour changes in edit form, recalculate price
  const handleEditTourChange = (newTourId) => {
    const tourData = getTourInfo(newTourId);
    const price = tourData?.price || 0;
    setEditForm(p => ({ ...p, tour_id: newTourId, total_price: price }));
  };

  const handleSaveEdit = async () => {
    if (!editBooking) return;
    const total = (editForm.adults || 1) + (editForm.children || 0);
    if (total > 4) { toast.error('Máximo 4 pasajeros por tuk-tuk'); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      await axios.put(`${API}/admin/bookings/${editBooking.id}`, payload, { headers });
      toast.success('Reserva actualizada');
      setEditBooking(null);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al actualizar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editBooking) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/bookings/${editBooking.id}`, { headers });
      toast.success('Reserva eliminada');
      setEditBooking(null);
      setShowDeleteConfirm(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar reserva');
    } finally {
      setSaving(false);
    }
  };

  // === DRAG & DROP ===
  const handleDragStart = (e, booking) => {
    setDragBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragBooking(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dateStr, hour) => {
    e.preventDefault();
    if (!dragBooking) return;
    const newTime = hour || dragBooking.time;
    if (dragBooking.date === dateStr && dragBooking.time === newTime) return;

    try {
      const payload = { date: dateStr, time: newTime };
      if (dragBooking.tuk_tuk_id) payload.tuk_tuk_id = dragBooking.tuk_tuk_id;
      await axios.put(`${API}/admin/bookings/${dragBooking.id}`, payload, { headers });
      toast.success('Reserva movida');
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al mover reserva';
      toast.error(msg);
    }
    setDragBooking(null);
  };

  // === CREATE BOOKING ===
  const handleCreateBooking = async () => {
    if (!newBooking.name || !newBooking.date || !newBooking.time) {
      toast.error('Completa nombre, fecha y hora'); return;
    }
    try {
      const payload = { ...newBooking };
      if (!payload.tuk_tuk_id) delete payload.tuk_tuk_id;
      await axios.post(`${API}/admin/bookings/manual`, payload, { headers });
      toast.success('Reserva creada');
      setShowNewBooking(false);
      setNewBooking({ tour_id: tours[0]?.id || '', date: '', time: '10:00', adults: 1, children: 0, name: '', email: '', phone: '', notes: '', payment_method: 'cash', tuk_tuk_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear reserva');
    }
  };

  const handleBlockSlot = async () => {
    if (!blockForm.date) { toast.error('Selecciona fecha'); return; }
    try {
      await axios.post(`${API}/admin/availability/block`, {
        tour_id: blockForm.tour_id || null, date: blockForm.date,
        time: blockForm.time || null, reason: blockForm.reason || null
      }, { headers });
      toast.success('Franja bloqueada');
      setShowBlockModal(false);
      setBlockForm({ tour_id: '', date: '', time: '', reason: '' });
      fetchData();
    } catch (err) { toast.error('Error al bloquear'); }
  };

  const handleUnblock = async (blockId) => {
    try {
      await axios.delete(`${API}/admin/availability/block/${blockId}`, { headers });
      toast.success('Bloqueo eliminado');
      fetchData();
    } catch (err) { toast.error('Error al desbloquear'); }
  };

  const getBookingsForDate = (dateStr) => bookings.filter(b => b.date === dateStr);
  const getBlocksForDate = (dateStr) => blocks.filter(b => b.date === dateStr);

  const formatTitle = () => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    if (view === 'month') return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === 'day') return currentDate.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const { start, end } = getDateRange();
    return `${start} — ${end}`;
  };

  // === MONTH VIEW ===
  const renderMonth = () => {
    const y = currentDate.getFullYear(); const m = currentDate.getMonth();
    const firstDay = new Date(y, m, 1); const lastDay = new Date(y, m + 1, 0);
    let startDay = firstDay.getDay() - 1; if (startDay < 0) startDay = 6;
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    const today = new Date().toISOString().split('T')[0];

    return (
      <div className="grid grid-cols-7 gap-px bg-[#E7E5E4] rounded-xl overflow-hidden">
        {WEEKDAYS.map(d => (
          <div key={d} className="bg-[#F5F5F4] py-2 text-center text-xs font-semibold text-[#57534E] uppercase">{d}</div>
        ))}
        {days.map((day, idx) => {
          if (day === null) return <div key={`e-${idx}`} className="bg-white min-h-[100px]" />;
          const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dayBookings = getBookingsForDate(dateStr);
          const dayBlocks = getBlocksForDate(dateStr);
          const isToday = dateStr === today;
          return (
            <div
              key={dateStr}
              className={`bg-white min-h-[100px] p-1.5 cursor-pointer hover:bg-[#FAFAF9] transition-colors ${isToday ? 'ring-2 ring-inset ring-[#C2410C]' : ''} ${dragBooking ? 'hover:bg-blue-50' : ''}`}
              onClick={() => { setCurrentDate(new Date(dateStr)); setView('day'); }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, dateStr, null)}
              data-testid={`cal-day-${dateStr}`}
            >
              <span className={`text-xs font-medium ${isToday ? 'bg-[#C2410C] text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-[#1C1917]'}`}>{day}</span>
              {dayBlocks.length > 0 && (
                <div className="mt-0.5 px-1 py-0.5 bg-red-50 rounded text-[10px] text-red-600 flex items-center gap-0.5">
                  <Ban className="h-2.5 w-2.5" /> Bloqueado
                </div>
              )}
              {dayBookings.slice(0, 3).map(b => <BookingChip key={b.id} b={b} compact onDragStart={handleDragStart} onDragEnd={handleDragEnd} onEdit={openEdit} />)}
              {dayBookings.length > 3 && <div className="text-[10px] text-[#78716C] mt-0.5 px-1">+{dayBookings.length - 3} más</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // === WEEK VIEW ===
  const renderWeek = () => {
    const { start } = getDateRange();
    const startDate = new Date(start + 'T00:00:00');
    const hours = Array.from({ length: 14 }, (_, i) => `${String(i + 8).padStart(2, '0')}:00`);
    const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(startDate); d.setDate(startDate.getDate() + i); return d; });
    const today = new Date().toISOString().split('T')[0];

    return (
      <div className="overflow-x-auto">
        <div className="grid grid-cols-8 gap-px bg-[#E7E5E4] rounded-xl overflow-hidden min-w-[800px]">
          <div className="bg-[#F5F5F4] py-2 text-center text-xs font-semibold text-[#57534E]">Hora</div>
          {weekDays.map(d => {
            const ds = d.toISOString().split('T')[0];
            return <div key={ds} className={`bg-[#F5F5F4] py-2 text-center text-xs font-semibold ${ds === today ? 'text-[#C2410C]' : 'text-[#57534E]'}`}>{WEEKDAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]} {d.getDate()}</div>;
          })}
          {hours.map(hour => (
            <React.Fragment key={hour}>
              <div className="bg-white py-2 px-2 text-xs text-[#78716C] text-right">{hour}</div>
              {weekDays.map(d => {
                const ds = d.toISOString().split('T')[0];
                const hb = getBookingsForDate(ds).filter(b => b.time === hour);
                const bl = getBlocksForDate(ds).filter(b => !b.time || b.time === hour);
                return (
                  <div
                    key={`${ds}-${hour}`}
                    className={`bg-white p-0.5 min-h-[40px] border-t border-[#F5F5F4] ${dragBooking ? 'hover:bg-blue-50' : ''}`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, ds, hour)}
                  >
                    {bl.length > 0 && <div className="bg-red-50 rounded px-1 text-[9px] text-red-500">Bloqueado</div>}
                    {hb.map(b => <BookingChip key={b.id} b={b} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onEdit={openEdit} />)}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // === DAY VIEW ===
  const renderDay = () => {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayBookings = getBookingsForDate(dateStr);
    const dayBlocks = getBlocksForDate(dateStr);
    const hours = Array.from({ length: 14 }, (_, i) => `${String(i + 8).padStart(2, '0')}:00`);
    const startHour = 8;

    // Calculate booking positions
    const getBookingStyle = (b) => {
      const [bh, bm] = (b.time || '10:00').split(':').map(Number);
      const tourData = tours.find(t => t.id === b.tour_id);
      const durHours = tourData?.duration_hours || 1;
      const top = ((bh - startHour) * 60 + bm);
      const height = durHours * 60;
      return { top, height, durHours };
    };

    return (
      <div>
        {dayBlocks.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700 font-medium flex items-center gap-2"><Ban className="h-4 w-4" /> Franjas bloqueadas:</p>
            {dayBlocks.map(b => (
              <div key={b.id} className="flex items-center justify-between mt-1">
                <span className="text-xs text-red-600">{b.time || 'Todo el día'} {b.reason && `— ${b.reason}`}</span>
                <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" onClick={() => handleUnblock(b.id)}><X className="h-3 w-3" /></Button>
              </div>
            ))}
          </div>
        )}
        <div className="relative" style={{ minHeight: `${14 * 60}px` }}>
          {/* Hour grid lines */}
          {hours.map((hour, idx) => {
            const blocked = dayBlocks.some(b => !b.time || b.time === hour);
            return (
              <div
                key={hour}
                className={`absolute w-full flex ${blocked ? 'bg-red-50/30' : ''}`}
                style={{ top: `${idx * 60}px`, height: '60px' }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, dateStr, hour)}
              >
                <span className="text-xs text-[#78716C] w-14 flex-shrink-0 pt-1 text-right pr-3">{hour}</span>
                <div className="flex-1 border-t border-[#F5F5F4]">
                  {dayBookings.filter(b => b.time === hour).length === 0 && !blocked && (
                    <button className="text-xs text-[#D6D3D1] hover:text-[#C2410C] flex items-center gap-1 mt-1 ml-1" onClick={() => { setNewBooking(prev => ({ ...prev, date: dateStr, time: hour })); setShowNewBooking(true); }}>
                      <Plus className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {/* Booking blocks spanning actual duration */}
          {dayBookings.map(b => {
            const { top, height, durHours } = getBookingStyle(b);
            const tc = getTourColor(b.tour_id);
            const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
            const statusDot = sc.bg === 'bg-green-100' ? 'bg-green-500' : sc.bg === 'bg-yellow-100' ? 'bg-yellow-500' : sc.bg === 'bg-red-100' ? 'bg-red-500' : 'bg-slate-400';
            const [bh, bm] = (b.time || '10:00').split(':').map(Number);
            const endMin = bh * 60 + bm + durHours * 60;
            const endH = String(Math.floor(endMin / 60)).padStart(2, '0');
            const endM = String(Math.round(endMin % 60)).padStart(2, '0');
            return (
              <div
                key={b.id}
                draggable
                onDragStart={(e) => handleDragStart(e, b)}
                onDragEnd={handleDragEnd}
                onClick={() => openEdit(b)}
                className={`absolute left-16 right-4 rounded-lg border-l-4 cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-[#C2410C]/30 transition-all overflow-hidden ${tc.bg} ${tc.border}`}
                style={{ top: `${top}px`, height: `${Math.max(height, 30)}px`, zIndex: 10 }}
                data-testid={`day-booking-${b.id}`}
              >
                <div className="p-2 h-full flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <GripVertical className="h-3 w-3 text-[#A8A29E] flex-shrink-0" />
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} />
                      <p className={`text-sm font-semibold truncate ${tc.text}`}>{b.name}</p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${sc.bg} ${sc.text}`}>{sc.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#57534E]">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{b.time} – {endH}:{endM}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{(b.adults || 0) + (b.children || 0)}</span>
                    {b.tuk_tuk_name && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{b.tuk_tuk_name}</span>}
                  </div>
                  {height >= 60 && (
                    <p className="text-xs text-[#78716C] truncate">{b.tour_name || b.tour_id}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6 lg:p-8" data-testid="admin-calendar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#1C1917]">Calendario</h1>
          <p className="text-[#57534E]">Gestiona reservas y disponibilidad — arrastra para mover</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => { setBlockForm({ tour_id: '', date: currentDate.toISOString().split('T')[0], time: '', reason: '' }); setShowBlockModal(true); }} data-testid="block-slot-btn">
            <Ban className="h-4 w-4 mr-2" /> Bloquear
          </Button>
          <Button className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full" onClick={() => { setNewBooking(prev => ({ ...prev, date: currentDate.toISOString().split('T')[0] })); setShowNewBooking(true); }} data-testid="new-booking-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva reserva
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-xl p-4 shadow-sm border border-[#E7E5E4]">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Hoy</Button>
          <Button variant="outline" size="sm" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <h2 className="font-display text-xl font-semibold text-[#1C1917] ml-2 capitalize">{formatTitle()}</h2>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[#C2410C]" />}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Filter className="h-4 w-4 text-[#78716C]" />
            <select value={tourFilter} onChange={(e) => setTourFilter(e.target.value)} className="text-sm border border-[#D6D3D1] rounded-lg px-2 py-1.5 bg-white" data-testid="tour-filter">
              <option value="all">Todos los tours</option>
              {tours.map(t => <option key={t.id} value={t.id}>{t.name?.es || t.name}</option>)}
            </select>
          </div>
          <div className="flex bg-[#F5F5F4] rounded-lg p-0.5">
            {['day', 'week', 'month'].map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === v ? 'bg-white text-[#1C1917] shadow-sm' : 'text-[#78716C] hover:text-[#1C1917]'}`} data-testid={`view-${v}`}>
                {v === 'day' ? 'Día' : v === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        {tours.map(t => { const c = getTourColor(t.id); return <div key={t.id} className="flex items-center gap-1.5"><div className={`w-3 h-3 rounded-full ${c.dot}`} /><span className="text-[#57534E]">{t.name?.es || t.name}</span></div>; })}
        <div className="flex items-center gap-3 ml-4 border-l border-[#E7E5E4] pl-4">
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5"><div className={`w-2 h-2 rounded-full ${v.bg === 'bg-green-100' ? 'bg-green-500' : v.bg === 'bg-yellow-100' ? 'bg-yellow-500' : v.bg === 'bg-red-100' ? 'bg-red-500' : 'bg-slate-400'}`} /><span className="text-[#78716C]">{v.label}</span></div>
          ))}
        </div>
      </div>

      {/* Calendar views */}
      {view === 'month' && renderMonth()}
      {view === 'week' && renderWeek()}
      {view === 'day' && renderDay()}

      {/* === EDIT BOOKING MODAL === */}
      <Dialog open={!!editBooking} onOpenChange={() => { setEditBooking(null); setShowDeleteConfirm(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-booking-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="h-5 w-5 text-[#C2410C]" /> Editar reserva
            </DialogTitle>
          </DialogHeader>
          {editBooking && (
            <div className="space-y-4">
              {/* Tour */}
              <div>
                <Label className="text-sm">Tour</Label>
                <select value={editForm.tour_id} onChange={(e) => handleEditTourChange(e.target.value)} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm" data-testid="edit-tour">
                  {tours.map(t => <option key={t.id} value={t.id}>{t.name?.es || t.name} — {t.price} €</option>)}
                </select>
              </div>
              {/* Price display */}
              <div className="bg-[#FDFBF7] border border-[#E7E5E4] rounded-lg px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm text-stone-600">Precio (por tuk-tuk)</span>
                <span className="text-lg font-bold text-[#C2410C]" data-testid="edit-price-display">{editForm.total_price || getTourInfo(editForm.tour_id)?.price || 0} €</span>
              </div>
              {/* Date + Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Fecha</Label>
                  <Input type="date" value={editForm.date} onChange={(e) => setEditForm(p => ({ ...p, date: e.target.value }))} data-testid="edit-date" />
                </div>
                <div>
                  <Label className="text-sm">Hora</Label>
                  <Input type="time" value={editForm.time} onChange={(e) => setEditForm(p => ({ ...p, time: e.target.value }))} data-testid="edit-time" />
                </div>
              </div>
              {/* Tuk-Tuk */}
              <div>
                <Label className="text-sm">Tuk-Tuk asignado</Label>
                <select value={editForm.tuk_tuk_id} onChange={(e) => setEditForm(p => ({ ...p, tuk_tuk_id: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm" data-testid="edit-tuktuk">
                  <option value="">Sin asignar</option>
                  {tukTuks.map(tt => <option key={tt.id} value={tt.id}>{tt.name} ({tt.capacity} plazas)</option>)}
                </select>
              </div>
              {/* Client name, email, phone */}
              <div>
                <Label className="text-sm">Nombre del cliente</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-email" />
                </div>
                <div>
                  <Label className="text-sm">Teléfono</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-phone" />
                </div>
              </div>
              {/* Passengers */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Adultos</Label>
                  <Input type="number" min={1} max={4} value={editForm.adults} onChange={(e) => setEditForm(p => ({ ...p, adults: parseInt(e.target.value) || 1 }))} data-testid="edit-adults" />
                </div>
                <div>
                  <Label className="text-sm">Niños</Label>
                  <Input type="number" min={0} max={3} value={editForm.children} onChange={(e) => setEditForm(p => ({ ...p, children: parseInt(e.target.value) || 0 }))} data-testid="edit-children" />
                </div>
                <div>
                  <Label className="text-sm">Sillas inf.</Label>
                  <Input type="number" min={0} value={editForm.child_seats_needed} onChange={(e) => setEditForm(p => ({ ...p, child_seats_needed: parseInt(e.target.value) || 0 }))} data-testid="edit-seats" />
                </div>
              </div>
              {(editForm.adults + editForm.children) > 4 && (
                <p className="text-xs text-red-500">Máximo 4 pasajeros por tuk-tuk</p>
              )}
              {/* Status */}
              <div>
                <Label className="text-sm">Estado</Label>
                <select value={editForm.status} onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm" data-testid="edit-status">
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_COLORS[s]?.label || s}</option>)}
                </select>
              </div>
              {/* Hotel */}
              <div>
                <Label className="text-sm">Hotel / Punto de recogida</Label>
                <Input value={editForm.hotel} onChange={(e) => setEditForm(p => ({ ...p, hotel: e.target.value }))} data-testid="edit-hotel" />
              </div>
              {/* Notes */}
              <div>
                <Label className="text-sm">Notas internas</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="edit-notes" />
              </div>
              <div>
                <Label className="text-sm">Peticiones especiales</Label>
                <Textarea value={editForm.special_requests} onChange={(e) => setEditForm(p => ({ ...p, special_requests: e.target.value }))} rows={2} />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-[#E7E5E4]">
                {!showDeleteConfirm ? (
                  <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)} data-testid="edit-delete-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Eliminar reserva
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2" data-testid="delete-confirm-section">
                    <p className="text-xs text-red-700">¿Seguro? No se puede deshacer.</p>
                    <Button size="sm" variant="destructive" onClick={handleDelete} disabled={saving} data-testid="delete-confirm-btn">
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Sí, eliminar'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowDeleteConfirm(false)}>No</Button>
                  </div>
                )}
                <Button onClick={handleSaveEdit} disabled={saving || (editForm.adults + editForm.children) > 4} className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full" data-testid="edit-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW BOOKING MODAL */}
      <Dialog open={showNewBooking} onOpenChange={setShowNewBooking}>
        <DialogContent className="max-w-md" data-testid="new-booking-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva reserva manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <select value={newBooking.tour_id} onChange={(e) => setNewBooking(p => ({ ...p, tour_id: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm" data-testid="manual-tour-select">
              {tours.map(t => <option key={t.id} value={t.id}>{t.name?.es || t.name} — {t.price} €</option>)}
            </select>
            {/* Price display */}
            <div className="bg-[#FDFBF7] border border-[#E7E5E4] rounded-lg px-4 py-2 flex items-center justify-between">
              <span className="text-sm text-stone-600">Precio (por tuk-tuk)</span>
              <span className="text-base font-bold text-[#C2410C]" data-testid="new-booking-price">{getTourInfo(newBooking.tour_id)?.price || 0} €</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" value={newBooking.date} onChange={(e) => setNewBooking(p => ({ ...p, date: e.target.value }))} data-testid="manual-date" />
              <Input type="time" value={newBooking.time} onChange={(e) => setNewBooking(p => ({ ...p, time: e.target.value }))} data-testid="manual-time" />
            </div>
            <select value={newBooking.tuk_tuk_id} onChange={(e) => setNewBooking(p => ({ ...p, tuk_tuk_id: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm" data-testid="manual-tuktuk">
              <option value="">Tuk-Tuk (opcional)</option>
              {tukTuks.map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
            </select>
            <Input placeholder="Nombre del cliente *" value={newBooking.name} onChange={(e) => setNewBooking(p => ({ ...p, name: e.target.value }))} data-testid="manual-name" />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" min="1" max={4 - newBooking.children} value={newBooking.adults} onChange={(e) => setNewBooking(p => ({ ...p, adults: parseInt(e.target.value) || 1 }))} placeholder="Adultos" data-testid="manual-adults" />
              <Input type="number" min="0" max={4 - newBooking.adults} value={newBooking.children} onChange={(e) => setNewBooking(p => ({ ...p, children: parseInt(e.target.value) || 0 }))} placeholder="Niños" />
            </div>
            <Input placeholder="Email" value={newBooking.email} onChange={(e) => setNewBooking(p => ({ ...p, email: e.target.value }))} />
            <Input placeholder="Teléfono" value={newBooking.phone} onChange={(e) => setNewBooking(p => ({ ...p, phone: e.target.value }))} />
            <Input placeholder="Notas internas" value={newBooking.notes} onChange={(e) => setNewBooking(p => ({ ...p, notes: e.target.value }))} data-testid="manual-notes" />
            <select value={newBooking.payment_method} onChange={(e) => setNewBooking(p => ({ ...p, payment_method: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm">
              <option value="cash">Efectivo</option>
              <option value="online">Pago online</option>
            </select>
            <Button onClick={handleCreateBooking} className="w-full bg-[#C2410C] hover:bg-[#9A3412] rounded-full" data-testid="manual-create-btn">
              <Plus className="h-4 w-4 mr-2" /> Crear reserva
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BLOCK MODAL */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Ban className="h-5 w-5 text-red-500" /> Bloquear franja</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="date" value={blockForm.date} onChange={(e) => setBlockForm(p => ({ ...p, date: e.target.value }))} data-testid="block-date" />
            <Input type="time" value={blockForm.time} onChange={(e) => setBlockForm(p => ({ ...p, time: e.target.value }))} placeholder="Vacío = todo el día" data-testid="block-time" />
            <select value={blockForm.tour_id} onChange={(e) => setBlockForm(p => ({ ...p, tour_id: e.target.value }))} className="w-full border border-[#D6D3D1] rounded-lg px-3 py-2 text-sm">
              <option value="">Todos los tours</option>
              {tours.map(t => <option key={t.id} value={t.id}>{t.name?.es || t.name}</option>)}
            </select>
            <Input placeholder="Motivo (opcional)" value={blockForm.reason} onChange={(e) => setBlockForm(p => ({ ...p, reason: e.target.value }))} />
            <Button onClick={handleBlockSlot} className="w-full bg-red-600 hover:bg-red-700 rounded-full" data-testid="block-confirm-btn">
              <Ban className="h-4 w-4 mr-2" /> Bloquear
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
